import { Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';

export interface AffiliateInfo {
  valid: boolean;
  name?: string;
  /** Optional per-affiliate banner text (e.g. "Dein 50 %-Rabatt … einmalig …"). */
  discount_label?: string;
}

/**
 * Affiliate program (influencer referrals with lifetime discount).
 * The slug from ?ref=<slug> is captured on first visit (see AppComponent),
 * stored in localStorage and sent along at registration as `referred_by`.
 */
@Injectable({ providedIn: 'root' })
export class AffiliateService {
  private static readonly REF_KEY = 'kk_ref';
  private static readonly BANNER_DISMISSED_KEY = 'kk_ref_banner_dismissed';

  /**
   * Currently active affiliate (the one that will be sent as `referred_by`).
   * Single source of truth for the app banner and the registration hint, so
   * that switching the code in the registration form also updates the banner.
   */
  readonly active = signal<{ slug: string; name: string; label: string } | null>(null);

  /** Marks a validated affiliate as active and persists it for registration. */
  setActive(slug: string, info: AffiliateInfo): void {
    const clean = (slug || '').trim().toLowerCase();
    if (!clean || !info.valid) return;
    this.storeRef(clean);
    this.active.set({
      slug: clean,
      name: info.name || clean,
      label: (info.discount_label || '').trim()
    });
  }

  /** Affiliate slug captured from ?ref= on first visit ('' if none). */
  getStoredRef(): string {
    try {
      return (localStorage.getItem(AffiliateService.REF_KEY) || '').trim().toLowerCase();
    } catch {
      return '';
    }
  }

  storeRef(slug: string): void {
    try {
      const clean = (slug || '').trim().toLowerCase();
      if (clean) localStorage.setItem(AffiliateService.REF_KEY, clean);
    } catch { /* storage unavailable — non-fatal */ }
  }

  isBannerDismissed(): boolean {
    try {
      return localStorage.getItem(AffiliateService.BANNER_DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  }

  dismissBanner(): void {
    try {
      localStorage.setItem(AffiliateService.BANNER_DISMISSED_KEY, '1');
    } catch { /* non-fatal */ }
  }

  /** Validates a slug/code against the backend; only exposes the display name. */
  async getInfo(slug: string): Promise<AffiliateInfo> {
    const clean = (slug || '').trim().toLowerCase();
    if (!clean) return { valid: false };
    try {
      const res = await fetch(
        `${environment.pocketbaseUrl}/api/custom/affiliate-info?slug=${encodeURIComponent(clean)}`
      );
      if (!res.ok) return { valid: false };
      return await res.json();
    } catch {
      return { valid: false };
    }
  }
}
